import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 24 — Current maths (fag-packet). 20,000 × £5 = £100,000.
 * Shown as a big animated equation with the cost number lit in red.
 */

export default function Scene24_CurrentMaths() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-current-maths">
      {/* Red wash backdrop — this is COST */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(139,26,46,0.20) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mx-auto"
        >
          Current maths
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-4xl lg:text-5xl text-white mt-6 mb-12"
          data-testid="current-maths-title"
        >
          What it{" "}
          <span style={{ color: "#c83a4f" }}>costs us</span> today.
        </motion.h2>

        {/* The equation — three boxes connected by × and = */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8">
          <Term label="Videos / year" delay={0.4}>
            <span className="tnum">
              <CountUp
                from={0}
                to={20000}
                duration={1.8}
                startDelay={0.6}
              />
            </span>
          </Term>

          <Operator symbol="×" delay={0.8} />

          <Term label="Edit cost" delay={1.0} accent="#c83a4f">
            <span className="tnum">
              <CountUp
                from={0}
                to={5}
                prefix="£"
                duration={1.4}
                startDelay={1.2}
              />
            </span>
          </Term>

          <Operator symbol="=" delay={1.6} />

          <Term
            label="Annual edit cost"
            delay={1.8}
            accent="#c83a4f"
            big
            testId="current-cost-total"
          >
            <span className="tnum">
              <CountUp
                from={0}
                to={100000}
                prefix="£"
                duration={2.4}
                startDelay={2.0}
              />
            </span>
          </Term>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 3.4 }}
          className="mt-12 inline-flex items-center gap-3 rounded-full px-5 py-3"
          style={{
            background: "rgba(200,58,79,0.10)",
            border: "1px solid rgba(200,58,79,0.32)",
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "#c83a4f" }}
          />
          <span className="text-sm tracking-[0.18em] uppercase" style={{ color: "#c83a4f" }}>
            £100,000 / year · gone on editing alone
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}

function Term({
  label,
  children,
  delay = 0,
  accent = "#e2e8f0",
  big = false,
  testId,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`rounded-2xl glass px-6 py-6 sm:px-8 sm:py-7 ${big ? "min-w-[280px]" : ""}`}
      data-testid={testId}
    >
      <div
        className="text-[10px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        {label}
      </div>
      <div
        className={`kn-display ${big ? "text-[60px] sm:text-[80px] lg:text-[96px]" : "text-[42px] sm:text-[54px] lg:text-[64px]"} leading-none whitespace-nowrap`}
        style={{
          color: accent,
          fontWeight: 800,
          textShadow: big ? `0 0 60px ${accent}55` : "none",
        }}
      >
        {children}
      </div>
    </motion.div>
  );
}

function Operator({ symbol, delay = 0 }) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white/45"
    >
      {symbol}
    </motion.span>
  );
}
